import * as React from "react";

export type CultureData = {
  culture: string;
  alternate_names?: string[];
  wikipedia_url?: string | null;
  location_description?: string;
  countries?: string[];
  coordinates?: { lat: number; lon: number } | null;
  geojson?: any | null;
  notes?: string;
};

interface GlobePoint {
  lat: number;
  lng: number;
  name: string;
  isSelected: boolean;
  culture: CultureData;
}

interface GlobePolygon {
  geometry: any;
  name: string;
  isSelected: boolean;
  culture: CultureData;
}

interface Globe3DMapProps {
  culturesData: CultureData[];
  culturesToHighlight: Set<string>;
  onCultureToggle: (culture: CultureData) => void;
  height?: number;
}

// Africa-centered initial view
const AFRICA_LAT = 2;
const AFRICA_LNG = 20;
const AFRICA_ALTITUDE = 1.8;

export const Globe3DMap: React.FC<Globe3DMapProps> = ({
  culturesData,
  culturesToHighlight,
  onCultureToggle,
  height = 500,
}) => {
  const [GlobeComponent, setGlobeComponent] = React.useState<any>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const globeRef = React.useRef<any>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [width, setWidth] = React.useState(600);

  // Dynamically import react-globe.gl to keep main bundle lean
  React.useEffect(() => {
    import("react-globe.gl")
      .then((mod) => {
        setGlobeComponent(() => mod.default);
        setIsLoading(false);
      })
      .catch((err) => {
        console.error("Failed to load react-globe.gl:", err);
        setLoadError("Failed to load 3D globe library.");
        setIsLoading(false);
      });
  }, []);

  // Track container width for responsive sizing
  React.useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setWidth(entry.contentRect.width || 600);
      }
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Once globe is ready, point it at Africa
  const handleGlobeReady = React.useCallback(() => {
    if (globeRef.current) {
      globeRef.current.pointOfView(
        { lat: AFRICA_LAT, lng: AFRICA_LNG, altitude: AFRICA_ALTITUDE },
        0
      );
      // Dim atmosphere for cleaner look
      globeRef.current.controls().autoRotate = false;
    }
  }, []);

  // Points for cultures without GeoJSON polygons
  const points: GlobePoint[] = React.useMemo(() => {
    return culturesData
      .filter((c) => c.coordinates && !c.geojson)
      .map((c) => ({
        lat: c.coordinates!.lat,
        lng: c.coordinates!.lon,
        name: c.culture,
        isSelected: culturesToHighlight.has(c.culture),
        culture: c,
      }));
  }, [culturesData, culturesToHighlight]);

  // Polygons for cultures that have GeoJSON
  const polygons: GlobePolygon[] = React.useMemo(() => {
    return culturesData
      .filter((c) => c.geojson)
      .map((c) => ({
        geometry: c.geojson,
        name: c.culture,
        isSelected: culturesToHighlight.has(c.culture),
        culture: c,
      }));
  }, [culturesData, culturesToHighlight]);

  if (isLoading) {
    return (
      <div
        ref={containerRef}
        className="flex items-center justify-center border border-theme-color"
        style={{ height }}
      >
        <p className="text-sm opacity-75 animate-pulse">
          Loading 3D globe...
        </p>
      </div>
    );
  }

  if (loadError || !GlobeComponent) {
    return (
      <div
        ref={containerRef}
        className="flex items-center justify-center border border-theme-color"
        style={{ height }}
      >
        <p className="text-sm opacity-75">{loadError ?? "Globe unavailable"}</p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      style={{ height, width: "100%", overflow: "hidden" }}
      className="border border-theme-color"
    >
      <GlobeComponent
        ref={globeRef}
        width={width}
        height={height}
        onGlobeReady={handleGlobeReady}
        // Basemap
        globeImageUrl="//unpkg.com/three-globe/example/img/earth-blue-marble.jpg"
        bumpImageUrl="//unpkg.com/three-globe/example/img/earth-topology.png"
        backgroundColor="rgba(0,0,0,0.92)"
        showAtmosphere={true}
        atmosphereColor="#4ecdc4"
        atmosphereAltitude={0.12}
        // Point markers (cultures without GeoJSON)
        pointsData={points}
        pointLat="lat"
        pointLng="lng"
        pointColor={(d: GlobePoint) =>
          d.isSelected ? "#ff6b6b" : "#4ecdc4"
        }
        pointAltitude={(d: GlobePoint) => (d.isSelected ? 0.06 : 0.015)}
        pointRadius={(d: GlobePoint) => (d.isSelected ? 0.55 : 0.35)}
        pointLabel={(d: GlobePoint) => `
          <div style="
            background: rgba(0,0,0,0.85);
            color: #fff;
            padding: 5px 9px;
            border-radius: 3px;
            font-family: monospace;
            font-size: 12px;
            border: 1px solid ${d.isSelected ? "#ff6b6b" : "#4ecdc4"};
          ">
            <strong>${d.name}</strong>
            ${d.culture.location_description
              ? `<br/><span style="opacity:0.75;font-size:11px">${d.culture.location_description}</span>`
              : ""
            }
            <br/><span style="opacity:0.5;font-size:10px">${d.isSelected ? "Click to deselect" : "Click to filter"}</span>
          </div>
        `}
        onPointClick={(d: GlobePoint) => onCultureToggle(d.culture)}
        pointsMerge={false}
        // GeoJSON polygon regions (cultures with polygons)
        polygonsData={polygons}
        polygonGeoJsonGeometry="geometry"
        polygonCapColor={(d: GlobePolygon) =>
          d.isSelected ? "rgba(255,107,107,0.55)" : "rgba(78,205,196,0.25)"
        }
        polygonSideColor={(d: GlobePolygon) =>
          d.isSelected ? "rgba(201,42,42,0.6)" : "rgba(45,156,219,0.3)"
        }
        polygonStrokeColor={(d: GlobePolygon) =>
          d.isSelected ? "#c92a2a" : "#2d9cdb"
        }
        polygonAltitude={(d: GlobePolygon) => (d.isSelected ? 0.015 : 0.003)}
        polygonLabel={(d: GlobePolygon) => `
          <div style="
            background: rgba(0,0,0,0.85);
            color: #fff;
            padding: 5px 9px;
            border-radius: 3px;
            font-family: monospace;
            font-size: 12px;
            border: 1px solid ${d.isSelected ? "#ff6b6b" : "#4ecdc4"};
          ">
            <strong>${d.name}</strong>
            ${d.culture.location_description
              ? `<br/><span style="opacity:0.75;font-size:11px">${d.culture.location_description}</span>`
              : ""
            }
            <br/><span style="opacity:0.5;font-size:10px">${d.isSelected ? "Click to deselect" : "Click to filter"}</span>
          </div>
        `}
        onPolygonClick={(d: GlobePolygon) => onCultureToggle(d.culture)}
      />
    </div>
  );
};
